// api
import { apiPostFurniture, apiGetUpptecToken } from '@/services/API/furniture'

// vuex
import {
  WARRANTY_MODULE_NAME,
  WARRANTY_UPDATE_CREATED,
  WARRANTY_GET_SINISTER_SUMMARY,
  WARRANTY_UPDATE_PROPS
} from '@/store/types'

import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import warrantyModule from '@/store/modules/warranty'

// components
import maWarrantyRef from '@/views/warrantyLayout/components/warrantyRef'
import maWarrantySticky from '@/views/warrantyLayout/components/warrantySticky'
import maEmpty from '@/components/empty'
import maEventBus from '@/components/eventbus'
import maInputRadio from '@/components/input/modules/radio'
import maInputBtn from '@/components/input/modules/btn'

// constants
import { kindOfPropertyOptions } from './forms'

/**
 * @module
 * @name Furniture
 */
export default {
  name: 'Furniture',

  /**
   * @property {Number} uptecIframeKey Used to force iframe reload when the
   *  sinister has changed.
   */
  data: function() {
    return {
      uptecIframeKey: 1,
      kindOfProperty: 'PROPIETAIRE',
      kindOfPropertyOptions,
      upptecToken: null
    }
  },

  components: {
    maWarrantyRef,
    maWarrantySticky,
    maEmpty,
    maInputBtn,
    maInputRadio
  },

  /**
   * @property {String} todayDate Returns YYYY-MM-DD
   *  language. It's got from a JS object.
   * @property {String} iframeUrl Iframe URL based on sinister id, today date and
   *  token parameter.
   * @property {Boolean} sinisterHasDamages True if sinister has damages.
   */
  computed: {
    ...mapState({
      agentView: state => state.agentView
    }),
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantySinisterId: 'sinisterId',
      stateWarrantySecondaryId: 'secondaryId',
      stateWarrantyCreated: 'created',
      stateWarrantySinisterSummary: 'sinisterSummary',
      stateWarrantyZipCode: 'zipCode',
      stateWarrantyPropertyType: 'propertyType',
      stateWarrantyResidentType: 'residentType',
      stateWarrantyMoreThanTwoYears: 'moreThanTwoYears',
      stateWarrantyCoordinates: 'coordinates'
    }),
    ...mapGetters(WARRANTY_MODULE_NAME, {
      getWarrantySinisterHasAddress: 'sinisterHasAddress'
    }),

    comWarrantyCreated: {
      get() {
        return this.stateWarrantyCreated
      },
      set(v) {
        this.mutWarrantyUpdateCreated(v)
      }
    },

    todayDate: function() {
      const d = new Date()
      return (
        [
          d.getFullYear(),
          ('0' + (d.getMonth() + 1)).slice(-2),
          ('0' + d.getDate()).slice(-2)
        ].join('-') || null
      )
    },

    iframeUrl: function() {
      if (!this.upptecToken || !this.stateWarrantySinisterId || !this.todayDate)
        return null
      return (
        'https://dialogue.plataforma-smart.com/damage/find' +
        `?name=${this.stateWarrantySinisterId}` +
        `&date=${this.todayDate}` +
        `&token=${this.upptecToken}`
      )
    },

    sinisterHasDamages: function() {
      return (
        this.stateWarrantySinisterSummary &&
        this.stateWarrantySinisterSummary.length > 0
      )
    },

    shouldShowRefs: function() {
      return !this.FNOLFlow.params.hideRef
    },

    externalDamageId: function() {
      return this.FNOLFlow.params.externalDamageId
        ? this.FNOLFlow.params.externalDamageId
        : null
    }
  },

  methods: {
    ...mapActions(WARRANTY_MODULE_NAME, {
      actWarrantyGetSinisterSummary: WARRANTY_GET_SINISTER_SUMMARY,
      actWarrantyUpdateProps: WARRANTY_UPDATE_PROPS
    }),
    ...mapMutations(WARRANTY_MODULE_NAME, {
      mutWarrantyUpdateCreated: WARRANTY_UPDATE_CREATED
    }),

    /**
     * @function
     * @name messageHandler
     * @param {Object} event Event object received from the iframe.
     * @description Handler used to post the received information from the iframe
     *  when an action is done, to backend.
     */
    messageHandler: function(event) {
      if (event.origin !== 'https://dialogue.plataforma-smart.com') {
        return
      }
      if (this._isDestroyed) {
        return
      }
      if ('claim_id' in event.data) {
        apiPostFurniture({
          sinisterId: this.stateWarrantySinisterId,
          claimId: event.data.claim_id,
          secondaryId: this.stateWarrantySecondaryId,
          externalDamageId: this.externalDamageId
        })
          .then(() => {
            const isCreated = this.comWarrantyCreated

            this.comWarrantyCreated = true

            if (!isCreated && this.getWarrantySinisterHasAddress) {
              this.actWarrantyUpdateProps()
            }

            this.actWarrantyGetSinisterSummary()
          })
          .catch(() => {
            this.comWarrantyCreated = false
          })
      }
    }
  },

  /**
   * @function
   * @name mounted
   * @description Attach handlers to manage iframe events, get api token Uptec, emit event to hide add damage button in warranty view
   */
  mounted: function() {
    /**
     * @function
     * @name apiGetUpptecToken
     * @description Get token for the Uptec iframe
     */
    apiGetUpptecToken().then(response => {
      this.upptecToken = response.upptec_token
    })

    /**
     * @event
     * @name iframeUptec:visible
     * @description When the iframe is visible, emit an event to hide add damage button in warranty view.
     */

    maEventBus.$emit('iframeUptec:visible')

    /**
     * @event
     * @name window.addEventListener
     * @description Add listener to iframe
     */
    window.addEventListener('message', this.messageHandler, false)

    /**
     * @event
     * @name damage:remove
     * @description Uptec remove items management. It will update the iframe on
     *  damage remove.
     */
    maEventBus.$on('damage:remove', () => {
      this.uptecIframeKey++
    })
  },

  /**
   * @function
   * @name beforeCreate
   * @description Register module if it's not registered.
   */
  /* beforeCreate: function () {
    if (!(WARRANTY_MODULE_NAME in this.$store.state)) {
      this.$store.registerModule(WARRANTY_MODULE_NAME, warrantyModule)
    }
  }, */

  /**
   * @function
   * @name beforeDestroy
   * @description Remove handler for iframe events.
   */
  beforeDestroy: function() {
    /**
     * @event
     * @name iframeUptec:hidden
     * @description When the iframe is hiden, emit an event to display the add damage button in warranty view.
     */
    maEventBus.$emit('iframeUptec:hidden')

    /**
     * @event
     * @name window.addEventListener
     * @description Remove listener to iframe
     */
    window.removeEventListener(this.messageHandler)
  }
}
